import { calendarEra, DAY_LENGTH_MS, isGregorian, monthName, monthNames, monthsInYear, msToParts, partsToMs, weekdayIndex, weekdayName, weekdayNames, yearText } from './calendar.js';
import type { StoryCtx } from './ctx.js';
import { narrativeOrder } from './narrative.js';
import { isScene } from './scene.js';
import { normalizeSceneTime } from './sceneTime.js';
import { visibleText } from './terms.js';
import { anchorMoment, defaultAnchor, sameAt, textAnchorOf, validAnchor } from './textTime.js';
import { impliedPrecision, markFamily, markMoment, momentOf, resolveMarks, timeFromMarks, timeMarksIn, type MarkInfo, type MarkRole, type Moment } from './timeMarks.js';
import { findTimeSuggestions, parseTimeInput } from './timeRecognize.js';
import type { IgnoredTerm, SceneTime, StoryNode, TimePrecision } from './types.js';

// ---------------------------------------------------------------------------
// Zeitmarken, die nicht mehr stimmen (PF 9, PLAN.md §16d): Dispositio zeigt, wo der Text und die Zeit der Szene auseinanderlaufen, und
// schlägt vor, wie der Text wieder passt – geändert wird nichts ungefragt. Drei Fälle:
//  - verschoben: Die Szene wurde auf der Zeitlinie gezogen bzw. ihre Zeit von Hand gesetzt; die Marke, die Beginn, Ende bzw. Dauer
//    bestimmt, ergibt eine andere Zeit. Vorschlag: ein Wortlaut, der die Zeit der Szene sagt („fünf Tage später“).
//  - Reihenfolge: Eine relative Marke („drei Tage später“) zählt ab ihrem Anker, gelesen wird sie aber nach einer anderen Szene
//    (umsortiert bzw. eine Szene dazwischen). Vorschlag: ein Wortlaut ab der Szene davor; die Zeit bleibt.
//  - Wortlaut: Der Text einer Marke sagt etwas anderes als ihr Wert (darin getippt). Vorschlag: der Wert, den der Text sagt.
//  - Wochentag (PF 23): Der Text nennt einen anderen Wochentag, als das Datum hat („Montag, der 5. März 1823“ war ein Mittwoch).
//    Vorschlag: der richtige Wochentag – oder das nächste Datum mit dem genannten.
// Der Wortlaut entsteht so nah wie möglich an dem des Autors (nur Zahl, Datum bzw. Tageszeit ausgetauscht) und gilt nur, wenn die
// Erkennung ihn genau so liest.
// ---------------------------------------------------------------------------

/** Eine Zeitmarke mit ihrem Text (wie er zu lesen ist) */
export type MarkAt = MarkInfo & { text: string };

/**
 * Vorschlag für eine Marke: ihr neuer Wert und ihr Text – mit neuem Wortlaut, bzw. unverändert, wenn er keine erkannte Zeitangabe
 * enthält (eigene Worte wie „Neujahr“). `anchor`: zugleich der neue Anker der Szene (Reihenfolge).
 */
export interface MarkFix {
  value: string;
  precision: TimePrecision | null;
  approx: boolean;
  role: MarkRole | null;
  text: string;
  anchor?: string;
}

/** Eine Zeitmarke, die nicht mehr stimmt; `index` ist ihre Stelle unter den Zeitmarken des Textes. `fix` null: kein Wortlaut gefunden. */
export type TimeConflict =
  | { kind: 'moved'; index: number; fix: MarkFix | null; textTime: SceneTime }
  | { kind: 'order'; index: number; fix: MarkFix | null; anchorId: string; previousId: string | null }
  | { kind: 'wording'; index: number; fix: MarkFix }
  | { kind: 'weekday'; index: number; fix: MarkFix; said: string; actual: string; dateFix: MarkFix | null };

// ---------------------------------------------------------------------------
// Werte umrechnen
// ---------------------------------------------------------------------------

const ABS = /^(\d{4})(?:-(\d{2})(?:-(\d{2})(?:T(\d{2}):(\d{2}))?)?)?$/;
const COUNT = /^(minute|stunde|tag|jahr)(-?\d+)(?:@(\d{2}):(\d{2}))?$/;
const REL = /^([+-])(\d{1,6})(min|h|d|w|mo|y)(?:@(\d{2}):(\d{2}))?$/;
const DURATION = /^(\d{1,6})(min|h|d|w|mo|y)$/;
const CLOCK_IN = /(?:T|@)(\d{2}):(\d{2})$/;
const UNIT_MINUTES: Record<string, number> = { min: 1, h: 60, d: 1440, w: 10080, mo: 43200, y: 525960 };
const PRECISIONS: TimePrecision[] = ['year', 'month', 'day', 'hour', 'minute'];

const pad = (n: number): string => String(n).padStart(2, '0');
const hhmm = (min: number): string => `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
const clockIn = (v: string): number | null => {
  const m = CLOCK_IN.exec(v);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
/** Minute des Tages (Tage beginnen in jedem Kalender bei einem Vielfachen eines Tages) */
const minutesOfDay = (m: Moment): number => {
  const minutes = m.basis === 'ms' ? Math.round(m.value / 60000) : m.value;
  return ((minutes % 1440) + 1440) % 1440;
};
const dayOf = (m: Moment): number => Math.floor(m.basis === 'ms' ? m.value / 86400000 : m.value / 1440);
const precisionOf = (m: MarkInfo): TimePrecision => m.precision ?? impliedPrecision(m.value);
/** Genauigkeit, in der ein Vorschlag die Zeit treffen muss: eine ungefähre Tageszeit („am Morgen“) nur den Tag */
const checkPrecision = (m: MarkInfo): TimePrecision => {
  const p = precisionOf(m);
  return m.approx && (p === 'hour' || p === 'minute') ? 'day' : p;
};

/** Tageszeiten, wie die Erkennung sie liest (Minuten des Tages) */
const PARTS: [string, number][] = [
  ['Morgen', 420],
  ['Vormittag', 600],
  ['Mittag', 720],
  ['Nachmittag', 900],
  ['Abend', 1140],
  ['Nacht', 1380],
];
const PART_OF = new Map(PARTS.map(([w, m]) => [m, w]));

/**
 * Uhrzeit des neuen Werts: Eine ungefähre (Tageszeit) bleibt, wenn sich der Tag ändert oder sie nahe liegt, sonst die nächste Tageszeit;
 * eine genaue ist die der Szene (in der Genauigkeit der Marke).
 */
function clockFor(mark: MarkInfo, target: Moment, dayChanged: boolean): string {
  const t = minutesOfDay(target);
  const orig = clockIn(mark.value);
  if (mark.approx && orig !== null) {
    if (dayChanged || Math.abs(t - orig) < 180) return hhmm(orig);
    return hhmm(PARTS.reduce((a, b) => (Math.abs(b[1] - t) < Math.abs(a[1] - t) ? b : a))[1]);
  }
  return hhmm(precisionOf(mark) === 'minute' ? t : Math.floor(t / 60) * 60);
}

const signed = (n: number, unit: string): string => `${n < 0 ? '-' : '+'}${Math.abs(n)}${unit}`;

/**
 * Wert einer Marke, mit dem sie `target` ergibt – in ihrer Art (Datum bleibt Datum, „+3d“ bleibt ein Abstand in Tagen); relativ gerechnet
 * ab `from`. `current`: wohin sie bisher zeigt (eine Tageszeit bleibt, wenn sich nur der Tag ändert). null, wenn es keinen gibt.
 */
export function rewriteMarkValue(mark: MarkInfo, current: Moment | null, target: Moment, from: Moment | null): string | null {
  const v = mark.value;
  const clock = (): string => clockFor(mark, target, !current || dayOf(current) !== dayOf(target));
  const family = markFamily(v);
  if (family === 'abs' || family === 'yearless') {
    if (target.basis !== 'ms') return null;
    const d = msToParts(target.value);
    const date = `${String(d.y).padStart(4, '0')}-${pad(d.mo)}-${pad(d.d)}`;
    const timed = clockIn(v) !== null;
    if (family === 'abs') {
      const m = ABS.exec(v)!;
      if (m[2] === undefined) return date.slice(0, 4);
      if (m[3] === undefined) return date.slice(0, 7);
      return timed ? `${date}T${clock()}` : date;
    }
    // Tag ohne Jahr, solange das Jahr aus dem Zusammenhang passt; sonst mit Jahr
    const yearless = `-${date.slice(4)}${timed ? `T${clock()}` : ''}`;
    const back = markMoment(yearless, from);
    return back && dayOf(back) === dayOf(target) ? yearless : `${date}${timed ? `T${clock()}` : ''}`;
  }
  if (family === 'count') {
    if (target.basis !== 'count') return null;
    const m = COUNT.exec(v)!;
    if (m[1] === 'tag') return `tag${dayOf(target)}${m[3] !== undefined ? `@${clock()}` : ''}`;
    return `${m[1]}${Math.floor(target.value / { jahr: 525960, stunde: 60, minute: 1 }[m[1] as 'jahr' | 'stunde' | 'minute'])}`;
  }
  if (family === 'rel') {
    if (!from || from.basis !== target.basis) return null;
    const m = REL.exec(v)!;
    const unit = m[3]!;
    const timed = (s: string): string => (m[4] !== undefined ? `${s}@${clock()}` : s);
    const days = dayOf(target) - dayOf(from);
    if (unit === 'd' || unit === 'w') return timed(unit === 'w' && days % 7 === 0 ? signed(days / 7, 'w') : signed(days, 'd'));
    if (unit === 'mo' || unit === 'y') {
      if (target.basis === 'ms') {
        const a = msToParts(from.value);
        const b = msToParts(target.value);
        const n = monthsInYear();
        const months = (b.y - a.y) * n + (b.mo - a.mo);
        const candidate = timed(unit === 'y' && months % n === 0 ? signed(months / n, 'y') : signed(months, 'mo'));
        const back = markMoment(candidate, from);
        if (back && sameAt(back, target, precisionOf(mark))) return candidate;
      }
      return timed(signed(days, 'd'));
    }
    const minutes = Math.round((target.value - from.value) / (target.basis === 'ms' ? 60000 : 1));
    return unit === 'h' ? signed(Math.round(minutes / 60), 'h') : signed(minutes, 'min');
  }
  return null;
}

/** Dauer von `start` bis `end` in der Einheit der bisherigen Dauer (sonst in Tagen, Stunden bzw. Minuten); null, wenn sie nicht positiv ist */
export function rewriteDuration(value: string, start: Moment, end: Moment): string | null {
  if (start.basis !== end.basis) return null;
  const minutes = Math.round((end.value - start.value) / (start.basis === 'ms' ? 60000 : 1));
  if (minutes <= 0) return null;
  for (const unit of [DURATION.exec(value)?.[2] ?? 'd', 'd', 'h', 'min']) if (minutes % UNIT_MINUTES[unit]! === 0) return `${minutes / UNIT_MINUTES[unit]!}${unit}`;
  return `${minutes}min`;
}

// ---------------------------------------------------------------------------
// Wortlaut (Deutsch, wie die Erkennung in timeRecognize.ts ihn liest)
// ---------------------------------------------------------------------------

const NUMBER_WORDS = ['null', 'eins', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun', 'zehn', 'elf', 'zwölf'];
type Case = 'acc' | 'dat';
const ONE: Record<Case, Record<string, string>> = {
  acc: { min: 'eine Minute', h: 'eine Stunde', d: 'einen Tag', w: 'eine Woche', mo: 'einen Monat', y: 'ein Jahr' },
  dat: { min: 'einer Minute', h: 'einer Stunde', d: 'einem Tag', w: 'einer Woche', mo: 'einem Monat', y: 'einem Jahr' },
};
const MANY: Record<Case, Record<string, string>> = {
  acc: { min: 'Minuten', h: 'Stunden', d: 'Tage', w: 'Wochen', mo: 'Monate', y: 'Jahre' },
  dat: { min: 'Minuten', h: 'Stunden', d: 'Tagen', w: 'Wochen', mo: 'Monaten', y: 'Jahren' },
};
const SINGLE: Record<string, string> = { min: 'Minute', h: 'Stunde', d: 'Tag', w: 'Woche', mo: 'Monat', y: 'Jahr' };
/** „einen Tag“, „drei Tage“, „nach fünf Tagen“, „14 Tage“ (bis zwölf in Worten, außer der Autor schrieb Ziffern: „1 Tag“, „3 Tage“) */
const amount = (n: number, unit: string, c: Case, digits = false): string =>
  digits ? `${n} ${n === 1 ? SINGLE[unit] : MANY[c][unit]}` : n === 1 ? ONE[c][unit]! : `${n <= 12 ? NUMBER_WORDS[n] : n} ${MANY[c][unit]}`;

const NUM = '(?:\\d{1,4}|zwanzig|dreißig|vierzig|fünfzig|hundert|einen|einem|einer|zwölf|sieben|sechs|eine|eins|zwei|drei|vier|fünf|acht|neun|zehn|elf|ein)';
const UNIT = '(?:minuten?|stunden?|tag(?:e|en)?|wochen?|monat(?:e|en)?|jahr(?:e|en)?)';
const AMOUNT_IN = new RegExp(`(?<![\\p{L}\\p{N}])(${NUM})(\\s+)(${UNIT})(?![\\p{L}\\p{N}])`, 'iu');
const LATER = /(?<![\p{L}])(später|danach|darauf)(?![\p{L}])/iu;
const EARLIER = /(?<![\p{L}])(zuvor|vorher|früher)(?![\p{L}])/iu;
const PART_IN = /(?<![\p{L}])(morgen|vormittag|mittag|nachmittag|abend|nacht)(?![\p{L}])/iu;
const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const alternatives = (names: readonly string[]): string => [...names].sort((a, b) => b.length - a.length).map(escapeRe).join('|');
/** Datum mit Monatsnamen im Kalender der Geschichte („3. März 1823“, „12. Frostmond 312 n. F.“) */
const dateIn = (): RegExp => {
  const era = calendarEra() ? `(?:\\s+${escapeRe(calendarEra())})?` : '';
  return new RegExp(`(\\d{1,2})\\.(\\s*)(${alternatives(monthNames())})(\\s+\\d{${isGregorian() ? 4 : '1,4'}}${era})?`, 'iu');
};
/** Ein Wochentag im Kalender der Geschichte */
const weekdayIn = (): RegExp | null => (weekdayNames().length > 0 ? new RegExp(`(?<![\\p{L}])(${alternatives(weekdayNames())})(?![\\p{L}])`, 'iu') : null);
/** Jahreszahl, wie sie im Text steht: gregorianisch wie gespeichert („1823“), sonst mit der Jahreszählung („312 n. F.“) */
const yearWord = (y: string): string => (isGregorian() ? y : yearText(Number(y)));
const NUMERIC_DATE_IN = /(\d{1,2})\.(\d{1,2})\.(\d{4})?/u;
const CLOCK_DIGITS_IN = /(\d{1,2}):(\d{2})/u;

/** Großer Anfangsbuchstabe wie im Vorbild („Drei Tage später“ → „Fünf Tage später“) */
const likeCase = (s: string, like: string): string => (/^\p{Lu}/u.test(like) ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Teile eines Werts: Tag (Datum bzw. Tag im Jahr), Abstand und Uhrzeit */
interface Parts {
  date: { y: string | null; m: number; d: number } | null;
  month: { y: string; m: number } | null;
  year: string | null;
  rel: { sign: '+' | '-'; n: number; unit: string } | null;
  duration: { n: number; unit: string } | null;
  clock: number | null;
}
function partsOf(value: string): Parts | null {
  const none: Parts = { date: null, month: null, year: null, rel: null, duration: null, clock: clockIn(value) };
  let m = ABS.exec(value);
  if (m) {
    if (m[2] === undefined) return { ...none, year: m[1]! };
    if (m[3] === undefined) return { ...none, month: { y: m[1]!, m: Number(m[2]) } };
    return { ...none, date: { y: m[1]!, m: Number(m[2]), d: Number(m[3]) } };
  }
  if ((m = /^--(\d{2})-(\d{2})/.exec(value))) return { ...none, date: { y: null, m: Number(m[1]), d: Number(m[2]) } };
  if ((m = REL.exec(value))) return { ...none, rel: { sign: m[1] as '+' | '-', n: Number(m[2]), unit: m[3]! } };
  if ((m = DURATION.exec(value))) return { ...none, duration: { n: Number(m[1]), unit: m[2]! } };
  return null;
}

/** Uhrzeit hinter einer Angabe: „ am Abend“, „ in der Nacht“, „ gegen 8“, „ um 07:30“ */
function clockTail(clock: number | null, approx: boolean): string {
  if (clock === null) return '';
  const part = approx ? PART_OF.get(clock) : undefined;
  if (part) return part === 'Nacht' ? ' in der Nacht' : ` am ${part}`;
  return approx ? ` gegen ${clock % 60 === 0 ? clock / 60 : hhmm(clock)}` : ` um ${hhmm(clock)}`;
}

/** Wortlaute für einen Wert, ohne Vorbild (die Erkennung prüft sie danach) */
function plainPhrases(value: string, approx: boolean): string[] {
  const p = partsOf(value);
  if (!p) return [];
  const tail = clockTail(p.clock, approx);
  const part = approx && p.clock !== null ? PART_OF.get(p.clock) : undefined;
  if (p.year) return [`im Jahr ${isGregorian() ? Number(p.year) : yearWord(p.year)}`];
  if (p.month) return [`im ${monthName(p.month.m)} ${yearWord(p.month.y)}`];
  if (p.date) {
    const day = `${p.date.d}. ${monthName(p.date.m)}${p.date.y ? ` ${yearWord(p.date.y)}` : ''}`;
    return [...(part && part !== 'Nacht' ? [`am ${part} des ${day}`] : []), `am ${day}${part === 'Nacht' ? ' gegen 23' : tail}`];
  }
  if (p.duration) return [`${amount(p.duration.n, p.duration.unit, 'acc')} lang`];
  if (!p.rel) return [];
  const { sign, n, unit } = p.rel;
  const out: string[] = [];
  if (unit === 'd' && n === 0) {
    if (part === 'Nacht') out.push('in derselben Nacht');
    else if (part) out.push(`am selben ${part}`);
    out.push(`am selben Tag${tail}`);
  } else if (unit === 'd' && n === 1 && sign === '+') {
    if (part && part !== 'Nacht') out.push(`am nächsten ${part}`);
    out.push(`am nächsten Tag${tail}`);
  }
  out.push(`${amount(n, unit, 'acc')} ${sign === '+' ? 'später' : 'zuvor'}${tail}`);
  return out;
}

/** Wortlaute nah am Vorbild: nur Zahl und Einheit, Richtung, Datum bzw. Tageszeit ausgetauscht */
function closePhrases(phrase: string, value: string): string[] {
  const p = partsOf(value);
  if (!p) return [];
  let s = phrase;
  if (p.rel || p.duration) {
    const { n, unit } = (p.rel ?? p.duration)!;
    const a = AMOUNT_IN.exec(s);
    if (!a) return [];
    if (p.rel?.sign === '-' && LATER.test(s)) s = s.replace(LATER, (w) => likeCase('zuvor', w));
    else if (p.rel?.sign === '+' && EARLIER.test(s)) s = s.replace(EARLIER, (w) => likeCase('später', w));
    else if (p.rel?.sign === '-' && /\bnach\s*$/i.test(s.slice(0, a.index))) return [];
    const c: Case = /nach\s*$/i.test(s.slice(0, a.index)) ? 'dat' : 'acc';
    s = s.slice(0, a.index) + likeCase(amount(n, unit, c, /^\d/.test(a[1]!)), a[1]!) + s.slice(a.index + a[0].length);
  } else if (p.date) {
    const d = dateIn().exec(s);
    const nd = d ? null : NUMERIC_DATE_IN.exec(s);
    if (d) {
      s = s.slice(0, d.index) + `${p.date.d}.${d[2]}${monthName(p.date.m)}${p.date.y ? ` ${yearWord(p.date.y)}` : ''}` + s.slice(d.index + d[0].length);
    } else if (nd) {
      const dd = nd[1]!.length === 2 ? pad(p.date.d) : String(p.date.d);
      const mm = nd[2]!.length === 2 ? pad(p.date.m) : String(p.date.m);
      s = s.slice(0, nd.index) + `${dd}.${mm}.${p.date.y ? (isGregorian() ? p.date.y : Number(p.date.y)) : ''}` + s.slice(nd.index + nd[0].length);
    } else return [];
    // ein Wochentag davor folgt dem neuen Datum („am Montag, dem 3. März“ → „am Mittwoch, dem 5. März“)
    const wd = weekdayIn();
    const at = p.date.y ? partsToMs({ y: Number(p.date.y), mo: p.date.m, d: p.date.d, h: 0, mi: 0 }) : null;
    const name = at !== null ? weekdayName(at) : null;
    if (wd && name) s = s.replace(wd, (w) => likeCase(name, w));
  } else return [];
  // Tageszeit bzw. Uhrzeit
  const part = p.clock !== null ? PART_OF.get(p.clock) : undefined;
  if (part && PART_IN.test(s)) s = s.replace(PART_IN, (w) => likeCase(part, w));
  else if (p.clock !== null && CLOCK_DIGITS_IN.test(s)) s = s.replace(CLOCK_DIGITS_IN, hhmm(p.clock));
  return [s];
}

/**
 * Neuer Text einer Marke für `value`: Enthält ihr Text genau eine erkannte Zeitangabe, wird sie ersetzt – erst nah am Wortlaut des Autors,
 * sonst schlicht („am 5. März 1823“, „fünf Tage später“); es gilt nur, was die Erkennung genau so liest. Ohne erkannte Zeitangabe (eigene
 * Worte) bleibt der Text. null: kein passender Wortlaut.
 */
export function rewordMark(mark: MarkAt, value: string): { text: string; precision: TimePrecision | null; approx: boolean } | null {
  const found = findTimeSuggestions(mark.text);
  if (found.length === 0) return { text: mark.text, precision: mark.precision, approx: mark.approx };
  if (found.length > 1) return null;
  const s = found[0]!;
  const phrase = mark.text.slice(s.from, s.to);
  for (const candidate of [...closePhrases(phrase, value), ...plainPhrases(value, mark.approx).map((c) => likeCase(c, phrase))]) {
    const r = parseTimeInput(candidate);
    if (r?.value === value) return { text: mark.text.slice(0, s.from) + candidate + mark.text.slice(s.to), precision: r.precision, approx: r.approx };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Wochentag gegen Datum (PF 23, Roadmap AP-40)
// ---------------------------------------------------------------------------

/**
 * Nennt der Text einer Marke mit vollem Datum (samt Jahr) einen anderen Wochentag, als das Datum im Kalender der Geschichte hat? Dann zwei
 * Auswege: `fix` – der Wochentag folgt dem Datum (der Wert bleibt); `dateFix` – das Datum rückt auf den nächsten Tag mit dem genannten
 * Wochentag (bei Gleichstand der spätere; null, wenn es dafür keinen Wortlaut gibt). null: passt, kein Wochentag oder kein volles Datum.
 */
export function weekdayConflict(mark: MarkAt): { said: string; actual: string; fix: MarkFix; dateFix: MarkFix | null } | null {
  const p = partsOf(mark.value);
  const wd = weekdayIn();
  const hit = wd?.exec(mark.text);
  if (!p?.date?.y || !hit) return null;
  const names = weekdayNames();
  const said = names.findIndex((n) => n.toLowerCase() === hit[1]!.toLowerCase());
  const at = partsToMs({ y: Number(p.date.y), mo: p.date.m, d: p.date.d, h: 0, mi: 0 });
  const actual = at === null ? null : weekdayIndex(at);
  if (said < 0 || actual === null || actual === said) return null;
  const keep = { precision: mark.precision, approx: mark.approx, role: mark.role ?? null };
  const fix: MarkFix = { value: mark.value, ...keep, text: mark.text.slice(0, hit.index) + likeCase(names[actual]!, hit[1]!) + mark.text.slice(hit.index + hit[1]!.length) };
  const forward = (said - actual + names.length) % names.length;
  const shift = forward <= names.length - forward ? forward : forward - names.length;
  const t = msToParts(at! + shift * DAY_LENGTH_MS);
  const value = `${String(t.y).padStart(4, '0')}-${pad(t.mo)}-${pad(t.d)}${mark.value.slice(10)}`;
  const reworded = rewordMark(mark, value);
  return { said: names[said]!, actual: names[actual]!, fix, dateFix: reworded ? { value, precision: reworded.precision, approx: reworded.approx, role: keep.role, text: reworded.text } : null };
}

// ---------------------------------------------------------------------------
// Wortlaut gegen Wert
// ---------------------------------------------------------------------------

/** Ein fester Bezug, um zwei Werte gleicher Art zu vergleichen */
const fixedMoment = (): Moment => ({
  basis: 'ms',
  value: isGregorian() ? Date.UTC(2001, 5, 15, 12, 0) : partsToMs({ y: 2001, mo: Math.ceil(monthsInYear() / 2), d: 1, h: 12, mi: 0 })!,
  unit: null,
});

/**
 * Sagen zwei Werte dasselbe? Verglichen in der gröberen Genauigkeit der beiden, ungefähre Uhrzeiten mit drei Stunden Spielraum („am
 * Morgen“ und 06:00). Ein Datum und ein Tag im Jahr gelten gleich, wenn Tag und Monat passen; sonst sind verschiedene Arten verschieden.
 */
export function sameMeaning(a: MarkInfo, b: MarkInfo): boolean {
  if (a.value === b.value) return true;
  let fa = markFamily(a.value);
  let fb = markFamily(b.value);
  if (!fa || !fb) return true;
  let [va, vb] = [a.value, b.value];
  if (fa !== fb && fa === 'abs' && fb === 'yearless') [vb, fb] = [va.slice(0, 4) + vb.slice(1), 'abs'];
  if (fa !== fb && fa === 'yearless' && fb === 'abs') [va, fa] = [vb.slice(0, 4) + va.slice(1), 'abs'];
  if (fa !== fb) return false;
  if (fa === 'duration') return Number(DURATION.exec(va)![1]) * UNIT_MINUTES[DURATION.exec(va)![2]!]! === Number(DURATION.exec(vb)![1]) * UNIT_MINUTES[DURATION.exec(vb)![2]!]!;
  const fixed = fixedMoment();
  const ma = markMoment(va, fixed);
  const mb = markMoment(vb, fixed);
  if (!ma || !mb) return va === vb;
  const pa = PRECISIONS.indexOf(precisionOf(a));
  const pb = PRECISIONS.indexOf(precisionOf(b));
  const p = PRECISIONS[Math.min(pa, pb)]!;
  if ((a.approx || b.approx) && (p === 'hour' || p === 'minute')) return ma.basis === mb.basis && Math.abs(ma.value - mb.value) < 180 * (ma.basis === 'ms' ? 60000 : 1);
  return sameAt(ma, mb, p);
}

/** Was die Erkennung im Text einer Marke liest (genau eine Zeitangabe), sonst null */
export function heardIn(text: string): MarkInfo | null {
  const found = findTimeSuggestions(text);
  return found.length === 1 ? found[0]! : null;
}

/** Dieselbe Uhrzeit? Ungefähre mit drei Stunden Spielraum, sonst in der gröberen Genauigkeit (Tag und gröber: gleich) */
function sameClock(a: MarkInfo, ca: number, b: MarkInfo, cb: number): boolean {
  if (a.approx || b.approx) return Math.abs(ca - cb) < 180;
  const p = PRECISIONS[Math.min(PRECISIONS.indexOf(precisionOf(a)), PRECISIONS.indexOf(precisionOf(b)))]!;
  return p === 'minute' ? ca === cb : p === 'hour' ? Math.floor(ca / 60) === Math.floor(cb / 60) : true;
}

/**
 * Sagt der Text einer Marke etwas anderes als ihr Wert? `heard`: was die Erkennung in ihm liest. Vorschlag: der Wert des Textes.
 * Werte verschiedener Art („gegen Abend“ = am selben Tag 19:00, als Marke ein Datum mit Uhrzeit) vergleicht nur, was beide sagen – die
 * Uhrzeit (Vorschlag: die Uhrzeit des Textes am selben Datum); ohne Uhrzeit nur, wenn der Text gerade geändert wurde (`edited`).
 */
export function wordingFix(mark: MarkAt, heard: MarkInfo | null, edited = false): MarkFix | null {
  if (!heard) return null;
  const fm = markFamily(mark.value);
  const fh = markFamily(heard.value);
  if (!fm || !fh) return null;
  const role = fh === 'duration' || mark.role === 'duration' ? null : (mark.role ?? null);
  const take = (value: string): MarkFix => ({ value, precision: heard.precision, approx: heard.approx, role, text: mark.text });
  const comparable = fm === fh || (fm === 'abs' && fh === 'yearless') || (fm === 'yearless' && fh === 'abs');
  if (comparable) return sameMeaning(mark, heard) ? null : take(heard.value);
  const cm = clockIn(mark.value);
  const ch = clockIn(heard.value);
  if (cm !== null && ch !== null) return sameClock(mark, cm, heard, ch) ? null : take(mark.value.replace(CLOCK_IN, (m) => `${m[0]}${hhmm(ch)}`));
  return edited ? take(heard.value) : null;
}

// ---------------------------------------------------------------------------
// Zeit der Szene gegen ihre Zeitmarken
// ---------------------------------------------------------------------------

/** Verschoben: die Marken, die Beginn bzw. Ende bestimmen, mit einem Vorschlag, der die Zeit der Szene trifft */
function movedConflicts(ctx: StoryCtx, scene: StoryNode, marks: readonly MarkAt[]): TimeConflict[] {
  const t = scene.time!;
  const anchorId = textAnchorOf(ctx, scene);
  const anchor = anchorId && validAnchor(ctx, scene, anchorId) ? anchorId : null;
  const base = anchor ? anchorMoment(ctx, anchor) : null;
  const r = resolveMarks(marks, base);
  const { time: text, usesBase } = timeFromMarks(marks, base);
  const S = momentOf(t.start);
  const A = momentOf(text?.start);
  if (!text || !S || !A) return [];
  const p = text.precision ?? 'minute';
  const E = momentOf(t.end);
  const C = momentOf(text.end);
  const startOff = !sameAt(A, S, p);
  const endOff = !!C && (!E || !sameAt(C, E, p));
  if (!startOff && !endOff) return [];
  const textTime = normalizeSceneTime({ ...text, ...(usesBase && anchor ? { anchor: { scene: anchor } } : {}) }) ?? text;
  const fixed: MarkAt[] = marks.map((m) => ({ ...m }));
  const touched: number[] = [];
  if (startOff) {
    const v = rewriteMarkValue(marks[r.start]!, r.marks[r.start]!.moment, S, r.marks[r.start]!.from);
    if (v) fixed[r.start] = { ...fixed[r.start]!, value: v };
    touched.push(r.start);
  }
  if (endOff) {
    // ein Ende relativ zum Beginn folgt dem neuen Beginn von selbst
    const mid = timeFromMarks(fixed, base).time;
    const follows = !!E && !!mid?.end && sameAt(momentOf(mid.end)!, E, p);
    const r2 = resolveMarks(fixed, base);
    const i = r2.end >= 0 ? r2.end : r2.duration;
    if (!follows && i >= 0) {
      const v = !E ? null : i === r2.end ? rewriteMarkValue(fixed[i]!, r2.marks[i]!.moment, E, r2.marks[i]!.from) : rewriteDuration(fixed[i]!.value, S, E);
      if (v) fixed[i] = { ...fixed[i]!, value: v };
      if (!touched.includes(i)) touched.push(i);
    }
  }
  const q = checkPrecision(marks[r.start]!);
  const check = timeFromMarks(fixed, base).time;
  const fits = !!check && sameAt(momentOf(check.start)!, S, q) && (!endOff || (!!E && !!check.end && sameAt(momentOf(check.end)!, E, q)));
  return touched
    .sort((a, b) => a - b)
    .map((i) => {
      const m = marks[i]!;
      const value = fixed[i]!.value;
      const w = fits && value !== m.value ? rewordMark(m, value) : null;
      return { kind: 'moved', index: i, textTime, fix: w && { value, precision: w.precision, approx: w.approx, role: m.role ?? null, text: w.text } };
    });
}

/** Reihenfolge: die relative Marke, die vom Anker ausgeht, wenn der Anker nicht mehr die Szene davor in Lesereihenfolge ist */
function orderConflicts(ctx: StoryCtx, scene: StoryNode, marks: readonly MarkAt[]): TimeConflict[] {
  const t = scene.time!;
  const stored = t.anchor?.scene;
  if (!stored || t.anchor?.offset || !validAnchor(ctx, scene, stored)) return [];
  if (!narrativeOrder(ctx).scenes.some((s) => s.scene.id === scene.id)) return [];
  const previous = defaultAnchor(ctx, scene)?.id ?? null;
  if (previous === stored) return [];
  const r = resolveMarks(marks, anchorMoment(ctx, stored));
  const i = r.marks.findIndex((m) => m.usedBase && m.moment);
  if (i < 0) return [];
  const mark = marks[i]!;
  const current = r.marks[i]!.moment!;
  const base = previous && validAnchor(ctx, scene, previous) ? anchorMoment(ctx, previous) : null;
  // ab der Szene davor gelesen ergibt die Marke dasselbe: kein Widerspruch
  const asRead = base ? markMoment(mark.value, base) : null;
  if (asRead && sameAt(asRead, current, checkPrecision(mark))) return [];
  let fix: MarkFix | null = null;
  const v = base ? rewriteMarkValue(mark, current, current, base) : null;
  const back = v && base ? markMoment(v, base) : null;
  if (v && back && sameAt(back, current, checkPrecision(mark))) {
    const w = rewordMark(mark, v);
    if (w) fix = { value: v, precision: w.precision, approx: w.approx, role: mark.role ?? null, text: w.text, anchor: previous! };
  }
  return [{ kind: 'order', index: i, fix, anchorId: stored, previousId: previous }];
}

/**
 * Zeitmarken im Draft einer Szene, die nicht mehr zu ihrer Zeit bzw. zur Lesereihenfolge passen (`marks`: die Marken des Textes in
 * Textreihenfolge, wie der Editor sie gerade hat). Verschoben gilt, wenn die Zeit nicht aus dem Text kommt (gezogen, von Hand); die
 * Reihenfolge, wenn sie aus dem Text kommt und an einem Anker hängt.
 */
export function sceneTimeConflicts(ctx: StoryCtx, scene: StoryNode, marks: readonly MarkAt[]): TimeConflict[] {
  const t = scene.time;
  if (!isScene(scene) || !t?.start || marks.length === 0) return [];
  return t.source === 'text' ? orderConflicts(ctx, scene, marks) : movedConflicts(ctx, scene, marks);
}

/** Die Zeitmarken eines gespeicherten Textes mit ihrem Text, wie er zu lesen ist */
export const marksWithText = (text: string): MarkAt[] =>
  timeMarksIn(text).map((m) => ({ value: m.value, precision: m.precision, approx: m.approx, role: m.role, text: visibleText(text.slice(m.textFrom, m.textTo)).text }));

// ---------------------------------------------------------------------------
// „Ist Absicht“: gemerkt im Dokument wie abgelehnte Vorschläge (`ignored`, siehe pruneIgnored)
// ---------------------------------------------------------------------------

const cut = (s: string): string => s.slice(0, 60);

/**
 * Eintrag für „ist Absicht“: verschoben gilt für genau diese Zeit der Szene, die Reihenfolge für genau diesen Anker und diese Szene
 * davor, der Wortlaut für diesen Text mit diesem Wert. Ändert sich etwas davon, erscheint der Widerspruch wieder.
 */
export function timeConflictIgnore(c: TimeConflict, scene: Pick<StoryNode, 'time'> | null, mark: MarkAt): IgnoredTerm {
  if (c.kind === 'moved') return { term: '~zeit', before: '~verschoben', after: cut(`${scene?.time?.start ?? ''}|${scene?.time?.end ?? ''}`) };
  if (c.kind === 'order') return { term: '~zeit', before: '~anker', after: cut(`${c.anchorId.slice(0, 24)}>${(c.previousId ?? '-').slice(0, 24)}`) };
  if (c.kind === 'weekday') return { term: cut(`~${mark.text}`), before: '~wochentag', after: cut(mark.value) };
  return { term: cut(`~${mark.text}`), before: '~wortlaut', after: cut(`${mark.value}>${c.fix.value}`) };
}

export const isTimeConflictIgnored = (ignored: readonly IgnoredTerm[] | undefined, entry: IgnoredTerm): boolean =>
  !!ignored?.some((x) => x.term === entry.term && x.before === entry.before && x.after === entry.after);

/** Ist „die Zeit passt nicht mehr zum Text“ für die jetzige Zeit der Szene als Absicht gemerkt? */
export const timeMismatchIgnored = (scene: StoryNode, ignored: readonly IgnoredTerm[] | undefined): boolean =>
  isTimeConflictIgnored(ignored, { term: '~zeit', before: '~verschoben', after: cut(`${scene.time?.start ?? ''}|${scene.time?.end ?? ''}`) });
